/**
 * User list layout class
 *
 * @package    Layouts
 * @author     Roman Kritskiy <rkritskiy@mindk.com>
 * @copyright  Copyright (c) 2010-2018 MindK (http://mindk.com). All rights reserved.
 * @license    http://mindk.com
 * @link       http://mindk.com
 */

import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { load, updateFilter, loadAutocomplete, loadRoles, loadJournalsAutocomplete } from 'Actions/user';
import { pageTitleDecorator, authenticatedPageDecorator } from 'Decorators';

import DataTable from 'Components/DataTable';
import Button from 'Common/CustomButtons/Button';
import Header from 'Components/Header';

import { QueryFilterType, UserType, DataTableMetadataType, RoleType, JournalType } from 'Types';
import {
    DATA_TABLE_COLUMN_TYPE_STATUS,
    DATA_TABLE_FILTER_TYPE_AUTOCOMPLETE_SINGLE,
    DATA_TABLE_COLUMN_TYPE_HIDDEN,
    DATA_TABLE_FILTER_TYPE_SELECT,
    DATA_TABLE_COLUMN_TYPE_LINK,
    ROLE_ADMIN, DATA_TABLE_COLUMN_TYPE_DATE,
} from 'Constants';
import Loader from 'Common/Loader/Loader';

@authenticatedPageDecorator([ROLE_ADMIN])
@pageTitleDecorator('users')
@connect(
    store => ({
        loading: store.userList.loading,
        success: store.userList.success,
        error: store.userList.error,
        data: store.userList.data,
        filter: store.userList.filter,
        metadata: store.userList.metadata,
        dataAutocomplete: store.userListAutocomplete.data,
        rolesData: store.userListRoles.data,
        filterAutocomplete: store.userListAutocomplete.filter,
        journalsFilterAutocomplete: store.journalsAutocomplete.filter,
        journalsData: store.journalsAutocomplete.data,
        authUser: store.auth.user,
    }),
    dispatch => ({
        load: filter => dispatch(load(filter)),
        updateFilter: filter => dispatch(updateFilter(filter)),
        loadAutocomplete: filter => dispatch(loadAutocomplete(filter)),
        loadJournalsAutocomplete: filter => dispatch(loadJournalsAutocomplete(filter)),
        loadRoles: () => dispatch(loadRoles()),
        createUser: () => dispatch(push('/users/create')),
    })
)
export default class UserList extends PureComponent {
    /**
     * PropTypes
     */
    static propTypes = {
        loading: PropTypes.bool.isRequired,
        success: PropTypes.bool.isRequired,
        error: PropTypes.bool.isRequired,
        data: PropTypes.arrayOf(UserType),
        filter: QueryFilterType,
        metadata: DataTableMetadataType,
        load: PropTypes.func.isRequired,
        authUser: PropTypes.object.isRequired,
        updateFilter: PropTypes.func.isRequired,
        dataAutocomplete: PropTypes.arrayOf(UserType),
        rolesData: PropTypes.arrayOf(RoleType),
        journalsData: PropTypes.arrayOf(JournalType),
        filterAutocomplete: QueryFilterType,
        journalsFilterAutocomplete: QueryFilterType,
        loadAutocomplete: PropTypes.func,
        loadJournalsAutocomplete: PropTypes.func,
        loadRoles: PropTypes.func,
        createUser: PropTypes.func.isRequired,
    };

    /**
     * Component Did Mount
     */
    componentDidMount() {
        const { loadRoles, updateFilter } = this.props;
        loadRoles();
        updateFilter({ where: {}, has: {} });
    }

    getFilter = () => {
        const { filter } = this.props;

        //return filter.merge({ where: { id: { neq: authUser.id } } }, { deep: true });
        return filter;
    };

    loadOptions = input => {
        const { filterAutocomplete } = this.props;
        let conditions = [];
        input.split(' ').forEach(word => {
            if (word.length) {
                conditions.push({ first_name: { like: `%${word}%` } });
                conditions.push({ last_name: { like: `%${word}%` } });
            }
        });

        this.props.loadAutocomplete(filterAutocomplete.merge({ where: { or: conditions } }));
    };

    loadJournalsOptions = input => {
        const { journalsFilterAutocomplete } = this.props;
        const where = {
            title: { like: `%${input}%` },
        };
        const filter = journalsFilterAutocomplete.merge({
            where,
        });
        this.props.loadJournalsAutocomplete(filter);
    };

    getColumns = () => {
        const { dataAutocomplete, rolesData, journalsData } = this.props;

        return [
            {
                key: 'id',
                col: { width: '5%' },
                title: 'data_table.id',
                sortable: true,
            },
            {
                key: 'journals.id',
                relation: 'journals',
                title: 'data_table.journal_id',
                type: DATA_TABLE_COLUMN_TYPE_HIDDEN,
                filterable: true,
                filterType: DATA_TABLE_FILTER_TYPE_AUTOCOMPLETE_SINGLE,
                filterPlaceholder: 'data_table.filter.journal_id',
                options: journalsData.map(item => ({
                    value: item.id,
                    label: item.title,
                })),
                loadOptions: this.loadJournalsOptions,
            },
            {
                key: 'name',
                col: { width: '45%' },
                filterKey: 'id',
                title: 'data_table.name',
                type: DATA_TABLE_COLUMN_TYPE_LINK,
                link: {
                    to: '/users/:id',
                    placeholders: {
                        id: item => item.id,
                    },
                },
                options: dataAutocomplete.map(item => ({
                    value: item.id,
                    label: item.name,
                })),
                loadOptions: this.loadOptions,
                filterType: DATA_TABLE_FILTER_TYPE_AUTOCOMPLETE_SINGLE,
                sortable: true,
                filterable: true,
                filterPlaceholder: 'data_table.filter.name',
            },
            {
                key: 'email',
                col: { width: '40%' },
                title: 'data_table.email',
                type: 'string',
                sortable: true,
            },
            {
                key: 'is_active',
                col: { width: '10%' },
                title: 'data_table.is_active',
                type: DATA_TABLE_COLUMN_TYPE_STATUS,
                sortable: true,
            },
            {
                key: 'created_at',
                col: { width: '10%' },
                title: 'data_table.created_at',
                type: DATA_TABLE_COLUMN_TYPE_DATE,
                sortable: true,
            },
            {
                key: 'role_id',
                relation: 'userRoles',
                title: 'data_table.role_id',
                type: DATA_TABLE_COLUMN_TYPE_HIDDEN,
                filterable: true,
                filterType: DATA_TABLE_FILTER_TYPE_SELECT,
                filterPlaceholder: 'data_table.filter.role_id',
                options: rolesData.map(item => ({
                    value: item.id,
                    label: item.name,
                })),
            },
        ];
    };

    render() {
        const { data, load, metadata, updateFilter, createUser, loading } = this.props;

        return (
            <div className="wrapJournalList">
                {loading && <Loader/>}

                <Header title="title.users">
                    <Button color="primary" onClick={createUser}>
                        <FormattedMessage id="button.user.create.link" />
                    </Button>
                </Header>

                <DataTable
                    columns={this.getColumns()}
                    metadata={metadata}
                    data={data}
                    filter={this.getFilter()}
                    load={load}
                    updateFilter={updateFilter}
                    icon={false}
                />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/layouts/UsersList/UsersList.jsx